import { Banner } from "@/components/flexkit/Banner";
import { Text } from "@/components/podkit/typography/Text";
import { useEnvironment, useStartEnvironment } from "@/queries/environment-queries";
import { FileChangeList, type FileChangeListRef } from "@/routes/environments/code-changes/FileChangeList";
import { showStart } from "@/routes/environments/phase";
import { EnvironmentGitStatus } from "gitpod-next-api/gitpod/v1/environment_pb";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";

export type CodeChangesPanelRef = {
    scrollToFile: (filePath: string) => void;
    openFile: (filePath: string) => void;
};

export type CodeChangesPanelProps = {
    environmentId: string;
};

export const CodeChangesPanel = forwardRef<CodeChangesPanelRef, CodeChangesPanelProps>(({ environmentId }, ref) => {
    const { data: environment } = useEnvironment(environmentId);
    const { mutate: startEnvironment, isPending: isStarting } = useStartEnvironment();
    const [openedFiles, setOpenedFiles] = useState<string[]>([]);
    const fileChangeListRef = useRef<FileChangeListRef>(null);

    const gitStatus = environment?.status?.content?.git;
    const hasChanges = Boolean(gitStatus?.changedFiles.length);
    const shouldShowStartEnv = environment ? showStart(environment) : false;

    useImperativeHandle(ref, () => ({
        scrollToFile: (filePath: string) => {
            fileChangeListRef.current?.scrollToFile(filePath);
        },
        openFile: (filePath: string) => {
            // Open the file in the accordion
            if (!openedFiles.includes(filePath)) {
                setOpenedFiles((prev) => [...prev, filePath]);
            }
            // Then scroll to it
            fileChangeListRef.current?.scrollToFile(filePath);
        },
    }), [openedFiles]);

    const handleStartEnvironment = () => {
        if (environment) {
            startEnvironment({ environmentId: environment.id });
        }
    };

    if (!environment) {
        return null;
    }

    return (
        <div>
            <div className="flex w-full items-center justify-between py-2.5">
                <span className="mr-2 select-none text-start text-md font-medium">Code Changes</span>
            </div>
            {shouldShowStartEnv && hasChanges && (
                <Banner
                    className="mb-2"
                    variant="info"
                    text="Start the environment to view code changes."
                    action={{
                        text: "Start environment",
                        onClick: handleStartEnvironment,
                        loading: isStarting,
                        "data-tracking-id": "start-environment-code-changes-panel",
                    }}
                />
            )}
            {hasChanges ? (
                <FileChangeList
                    ref={fileChangeListRef}
                    environment={environment}
                    gitStatus={new EnvironmentGitStatus(gitStatus)}
                    openedFiles={openedFiles}
                    onOpenedFilesChange={setOpenedFiles}
                />
            ) : (
                <Text className="text-sm text-content-secondary">No uncommitted changes</Text>
            )}
        </div>
    );
});
CodeChangesPanel.displayName = "CodeChangesPanel";
